import { Injectable } from '@angular/core';

import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';

import { ErrorMessage } from '@library/common/http';

let ID = 0;

@Injectable({
   providedIn: 'root',
})
export class ErrorMessageService {
   /**
    * How long an `alert`-type message stays visible before it auto-dismisses
    * itself. `notification`-type messages (e.g. maintenance banners, which need
    * to persist for the duration of the maintenance window) and `error`-type
    * messages are untouched by this — they still require explicit dismissal.
    * Without this, a stale alert (e.g. a one-off DOCUMENT_NOT_FOUND from an
    * earlier, unrelated action) could sit in the global error bar indefinitely
    * and later appear alongside a completely unrelated error. See ANG-1395.
    */
   private static readonly ALERT_AUTO_DISMISS_MS = 30_000;

   messages: BehaviorSubject<ErrorMessage[]> = new BehaviorSubject( [] );

   /** Pending auto-dismiss timers for `alert`-type messages, keyed by message id. */
   private alertTimers = new Map<number, ReturnType<typeof setTimeout>>();

   get errors() {
      return this.messages.pipe( map( message => message.filter( m => m.type === 'error' )));
   }

   get none(): boolean {
      return !this.messages.getValue().length;
   }

   get maintenance(): boolean {
      const messages = this.messages.getValue()
         .filter( m => m.type === 'error' )
         .map( m => m.code )
         .filter( code => code === 'maintenance' );

      return !!messages.length;
   }

   reset(): void {
      console.log( 'error reset called' );
      this.clearAllAlertTimers();
      this.messages.next( [] );
   }

   resetErrors(): void {
      this.messages.next( this.messages.getValue().filter( e => e.type !== 'error' ));
   }

   clear( id: number ): void {
      this.clearAlertTimer( id );

      const ms = this.messages.getValue();
      this.messages.next( ms.filter( m => m.id !== id ));

      const message = ms.filter( m => m.id === id )[0];
      if ( message?.callback ) {
         message.callback();
      }
   }

   push( message: string ): void {
      const current = this.messages.getValue();
      if ( current.map( e => e.message ).includes( message )) { return; }

      current.push( new ErrorMessage({
         id: ++ID,
         type: 'error',
         code: 'Default',
         message,
         timestamp: new Date(),
      }));

      this.messages.next( current );
   }

   add( message: ErrorMessage ): void {
      const current = this.messages.getValue();
      if ( current.map( e => e.message ).includes( message.message )) { return; }

      const id = ++ID;
      this.messages.next([ ...current, ({ ...message, id, timestamp: message.timestamp ?? new Date() }) ]);

      if ( message.type === 'alert' ) {
         this.scheduleAlertAutoDismiss( id );
      }
   }

   private scheduleAlertAutoDismiss( id: number ): void {
      this.clearAlertTimer( id );
      this.alertTimers.set( id, setTimeout(() => this.clear( id ), ErrorMessageService.ALERT_AUTO_DISMISS_MS ));
   }

   private clearAlertTimer( id: number ): void {
      const timer = this.alertTimers.get( id );
      if ( timer ) {
         clearTimeout( timer );
         this.alertTimers.delete( id );
      }
   }

   private clearAllAlertTimers(): void {
      this.alertTimers.forEach( timer => clearTimeout( timer ));
      this.alertTimers.clear();
   }
}
